/* Orbith — Quotes
   Generate client quotes, save them to the dashboard for later editing,
   and export any quote as a PDF (browser print-to-PDF of the paper preview).
   Ported from the Claude Design prototype and wired to the /api/quotes backend. */
import React, { useState, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { I, Icon } from '../shared/ns-icons'
import { NSField, NSSelect } from '../shared/ns-kit'
import { nsConfirm } from '../shared/ns-confirm'
import LoadingSpinner from '../common/LoadingSpinner'
import '../../styles/admin-pages.css'

const BANNER = '/orbith-quote-banner.png'

// Orbith's own details — the default "From" block on a new quote.
const ORBITH_FROM = {
  name: 'Orbith',
  email: 'hello@marcoalcini.com',
  country: 'United Kingdom',
  phone: '+44 7427 41609',
  website: 'https://orbith.co.uk',
}

const CURRENCY = { '£': 'GBP', '$': 'USD', '€': 'EUR' }

// Shared column template so the list header and rows line up (fixed-width actions
// column — an `auto`/`fr` last column sizes per-row and knocks everything askew).
const QCOLS = '1.4fr 1.6fr 1fr 1fr 1fr 132px'

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/* ---------------- helpers ---------------- */
function money(n, currency) {
  const v = Number(n) || 0
  return `${currency}${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}
function quoteTotal(q) {
  return (q.items || []).reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0), 0)
}
function initials(name) {
  return (name || '').split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || 'Q'
}
function fmtDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso + 'T00:00:00')
  if (isNaN(d)) return iso
  return d.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' })
}

const QSTATUS_TONE = { draft: '', sent: 'info', accepted: 'success', declined: 'danger' }
const QSTATUS_LABEL = { draft: 'Draft', sent: 'Sent', accepted: 'Accepted', declined: 'Declined' }

// Map an API row (snake_case columns) to the nested shape the UI works with.
function toUI(row) {
  return {
    id: row.id,
    number: row.quote_number || '',
    status: row.status || 'draft',
    currency: row.currency || '£',
    issueDate: row.issue_date || '',
    from: {
      name: row.from_name || '', email: row.from_email || '',
      country: row.from_location || '', phone: row.from_phone || '', website: row.from_website || '',
    },
    to: { name: row.client_name || '', email: row.client_email || '', phone: row.client_phone || '' },
    items: (Array.isArray(row.items) ? row.items : []).map(it => ({
      desc: it.desc || '', sublines: it.sublines || [], qty: it.qty ?? 1, price: it.price ?? 0,
    })),
    notes: row.notes || '',
  }
}
// Map the UI shape back to the API payload.
function toAPI(q) {
  return {
    quote_number: q.number, status: q.status, currency: q.currency, issue_date: q.issueDate,
    from_name: q.from?.name, from_email: q.from?.email, from_location: q.from?.country,
    from_phone: q.from?.phone, from_website: q.from?.website,
    client_name: q.to?.name, client_email: q.to?.email, client_phone: q.to?.phone,
    items: q.items, notes: q.notes,
  }
}

/* ---------------- PDF export ---------------- */
/* Renders the paper off-screen, rasterises it with html2canvas, and saves it
   straight to an A4 PDF via jsPDF — a real download, no browser print dialog
   (so no URL / date / site-name headers). Libraries are loaded on demand. */
async function downloadQuotePdf(quote) {
  const toastId = toast.loading('Preparing PDF…')
  try {
    const [{ default: html2canvas }, jspdf] = await Promise.all([
      import('html2canvas'),
      import('jspdf'),
    ])
    const JsPDF = jspdf.jsPDF || jspdf.default

    // Off-screen host so the paper renders at full size (not display:none).
    const host = document.createElement('div')
    host.style.cssText = 'position:fixed; left:-10000px; top:0; width:760px; background:#ffffff;'
    document.body.appendChild(host)
    const root = createRoot(host)
    root.render(<QuotePaper quote={quote} printMode />)

    // Wait a frame for React to paint, then for the banner image to decode.
    await new Promise(r => requestAnimationFrame(() => setTimeout(r, 30)))
    const img = host.querySelector('img')
    if (img && !img.complete) await new Promise(res => { img.onload = res; img.onerror = res })

    const paper = host.querySelector('.quote-paper') || host.firstChild
    if (paper && paper.style) { paper.style.boxShadow = 'none'; paper.style.borderRadius = '0' }
    const canvas = await html2canvas(paper, { scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false })
    root.unmount()
    host.remove()

    const pdf = new JsPDF({ unit: 'pt', format: 'a4' })
    const pageW = pdf.internal.pageSize.getWidth()
    const pageH = pdf.internal.pageSize.getHeight()
    const margin = 24
    const imgW = pageW - margin * 2
    const imgH = canvas.height * imgW / canvas.width
    const imgData = canvas.toDataURL('image/png')
    const pageContentH = pageH - margin * 2

    // Single page when it fits, otherwise slice across pages.
    let rendered = 0
    let first = true
    while (rendered < imgH - 0.5) {
      if (!first) pdf.addPage()
      pdf.addImage(imgData, 'PNG', margin, margin - rendered, imgW, imgH, undefined, 'FAST')
      rendered += pageContentH
      first = false
    }

    const safe = (quote.number || 'Orbith').replace(/[^\w-]+/g, '-')
    pdf.save(`Quote ${safe}.pdf`)
    toast.success('PDF downloaded', { id: toastId })
  } catch (e) {
    console.error('PDF export failed:', e)
    toast.error('Could not generate PDF', { id: toastId })
  }
}

function injectQuoteStyles() {
  if (document.getElementById('quotes-style')) return
  const s = document.createElement('style')
  s.id = 'quotes-style'
  s.textContent = `
    .qdp-pop { position: absolute; z-index: 60; top: calc(100% + 6px); left: 0; width: 258px; padding: 12px; box-shadow: var(--shadow-lg); }
    .qdp-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
    .qdp-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
    .qdp-day { height: 30px; border-radius: 8px; border: none; background: transparent; color: var(--text); font-size: 12.5px; font-weight: 500; cursor: pointer; transition: background 0.12s ease; }
    .qdp-day:hover { background: var(--surface-hover, var(--surface)); }
    .qdp-day.today { font-weight: 700; box-shadow: inset 0 0 0 1px var(--border); }
    .qdp-day.sel, .qdp-day.sel:hover { background: var(--accent); color: #fff; }
    .quote-paper {
      background: #fff; color: #17181F; border-radius: 14px;
      font-family: var(--font); width: 100%; max-width: 760px; margin: 0 auto;
      padding: 40px 46px 46px; box-shadow: var(--shadow-lg);
    }
    @media print {
      .quote-paper { box-shadow: none; border-radius: 0; max-width: none; padding: 0; margin: 0; }
    }
    .quote-paper .qp-banner { width: 100%; display: block; border-radius: 10px; margin-bottom: 26px; }
    .quote-paper .qp-tophead { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 22px; border-bottom: 1px solid #E3E5EE; margin-bottom: 26px; }
    .quote-paper .qp-brand { font-size: 13px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: #8A8FA3; }
    .quote-paper .qp-title { font-size: 26px; font-weight: 700; letter-spacing: -0.01em; margin-top: 14px; }
    .quote-paper .qp-num { font-size: 13px; color: #8A8FA3; margin-top: 4px; font-family: var(--font-mono); }
    .quote-paper .qp-parties { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin-bottom: 28px; }
    .quote-paper .qp-label { font-size: 10.5px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: #A6ABBD; margin-bottom: 8px; }
    .quote-paper .qp-partyname { font-size: 14px; font-weight: 600; margin-bottom: 3px; }
    .quote-paper .qp-partyline { font-size: 12.5px; color: #565B70; line-height: 1.5; }
    .quote-paper table { width: 100%; border-collapse: collapse; }
    .quote-paper thead th { text-align: left; font-size: 10.5px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: #A6ABBD; padding: 0 0 10px; border-bottom: 1px solid #E3E5EE; }
    .quote-paper thead th.num { text-align: right; }
    .quote-paper tbody td { padding: 14px 0; border-bottom: 1px solid #EEF0F5; vertical-align: top; font-size: 13.5px; }
    .quote-paper tbody td.num { text-align: right; font-family: var(--font-mono); white-space: nowrap; }
    .quote-paper .qp-itemname { font-weight: 600; }
    .quote-paper .qp-subline { font-size: 12px; color: #7B8198; margin-top: 2px; }
    .quote-paper .qp-totalrow td { border-bottom: none; padding-top: 18px; font-weight: 700; font-size: 15px; }
    .quote-paper .qp-notes { margin-top: 30px; }
    .quote-paper .qp-thanks { margin-top: 24px; font-size: 13px; color: #565B70; }
  `
  document.head.appendChild(s)
}

/* ---------------- custom date picker ---------------- */
function CalIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  )
}

function NSDatePicker({ value, onChange }) {
  const [open, setOpen] = useState(false)
  const wrapRef = React.useRef(null)
  const selected = value ? new Date(value + 'T00:00:00') : null
  const [view, setView] = useState(() => (selected && !isNaN(selected) ? new Date(selected) : new Date()))

  useEffect(() => {
    if (!open) return
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
  }, [open])

  const toggle = () => {
    if (!open && selected && !isNaN(selected)) setView(new Date(selected))
    setOpen(o => !o)
  }

  const y = view.getFullYear(), m = view.getMonth()
  const startOffset = (new Date(y, m, 1).getDay() + 6) % 7 // Monday-first
  const daysInMonth = new Date(y, m + 1, 0).getDate()
  const cells = [...Array(startOffset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)]

  const pick = (d) => {
    const iso = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    onChange(iso); setOpen(false)
  }
  const today = new Date()
  const isSel = (d) => selected && !isNaN(selected) && selected.getFullYear() === y && selected.getMonth() === m && selected.getDate() === d
  const isToday = (d) => today.getFullYear() === y && today.getMonth() === m && today.getDate() === d

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button type="button" className="input" onClick={toggle}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
        <span style={{ color: value ? 'var(--text)' : 'var(--text-faint)' }}>{value ? fmtDate(value) : 'Select date'}</span>
        <span style={{ color: 'var(--text-muted)', display: 'flex' }}><CalIcon /></span>
      </button>
      {open && (
        <div className="card qdp-pop">
          <div className="qdp-head">
            <button type="button" className="site-action" onClick={() => setView(new Date(y, m - 1, 1))}>‹</button>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{MONTHS[m]} {y}</div>
            <button type="button" className="site-action" onClick={() => setView(new Date(y, m + 1, 1))}>›</button>
          </div>
          <div className="qdp-grid" style={{ marginBottom: 4 }}>
            {WEEKDAYS.map(w => <div key={w} style={{ textAlign: 'center', fontSize: 10, color: 'var(--text-faint)', fontWeight: 600 }}>{w}</div>)}
          </div>
          <div className="qdp-grid">
            {cells.map((d, i) => d === null
              ? <div key={i} />
              : <button key={i} type="button" onClick={() => pick(d)} className={`qdp-day${isSel(d) ? ' sel' : ''}${isToday(d) && !isSel(d) ? ' today' : ''}`}>{d}</button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/* ---------------- the quote "paper" — shared by live preview + print ---------------- */
function QuotePaper({ quote, printMode }) {
  const c = quote.currency || '£'
  const total = quoteTotal(quote)
  return (
    <div className="quote-paper" style={printMode ? {} : undefined}>
      <img className="qp-banner" src={BANNER} alt="Orbith" />
      <div className="qp-tophead">
        <div>
          <div className="qp-brand">Quote</div>
          <div className="qp-title">{quote.number || '—'}</div>
          <div className="qp-num">Issued {fmtDate(quote.issueDate)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em' }}>{quote.from?.name || '—'}</div>
          <div className="qp-partyline">{quote.from?.website?.replace(/^https?:\/\//, '')}</div>
        </div>
      </div>

      <div className="qp-parties">
        <div>
          <div className="qp-label">From</div>
          <div className="qp-partyname">{quote.from?.name}</div>
          <div className="qp-partyline">{quote.from?.email}</div>
          <div className="qp-partyline">{quote.from?.country}</div>
          <div className="qp-partyline">{quote.from?.phone}</div>
        </div>
        <div>
          <div className="qp-label">To</div>
          <div className="qp-partyname">{quote.to?.name || '—'}</div>
          {quote.to?.email && <div className="qp-partyline">{quote.to.email}</div>}
          {quote.to?.phone && <div className="qp-partyline">{quote.to.phone}</div>}
        </div>
        <div>
          <div className="qp-label">Status</div>
          <div className="qp-partyname">{QSTATUS_LABEL[quote.status] || 'Draft'}</div>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>Description</th>
            <th className="num">Qty</th>
            <th className="num">Price</th>
            <th className="num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {(quote.items || []).map((it, i) => (
            <tr key={i}>
              <td>
                <div className="qp-itemname">{it.desc || '—'}</div>
                {(it.sublines || []).filter(Boolean).map((s, j) => <div className="qp-subline" key={j}>{s}</div>)}
              </td>
              <td className="num">{it.qty}</td>
              <td className="num">{money(it.price, c)}</td>
              <td className="num">{money((Number(it.qty) || 0) * (Number(it.price) || 0), c)}</td>
            </tr>
          ))}
          <tr className="qp-totalrow">
            <td colSpan={3} style={{ textAlign: 'right' }}>Total</td>
            <td className="num">{money(total, c)}</td>
          </tr>
        </tbody>
      </table>

      {quote.notes && (
        <div className="qp-notes">
          <div className="qp-label">Notes</div>
          <div className="qp-partyline">{quote.notes}</div>
        </div>
      )}
      <div className="qp-thanks">Thank you for considering {quote.from?.name || 'us'} for your business!</div>
    </div>
  )
}

/* ---------------- small stat card ---------------- */
function MiniStat({ label, value, icon, tone }) {
  return (
    <div className="card card-pad-sm" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <span className={`icon-tile ${tone || ''}`}><Icon name={icon} /></span>
      <div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)', marginTop: 2 }}>{value}</div>
      </div>
    </div>
  )
}

/* ---------------- page (list + editor) ---------------- */
export default function QuotesManager() {
  useEffect(() => { injectQuoteStyles() }, [])
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(null) // null | UI quote object
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')

  const { data, isLoading } = useQuery(['quotes'], () => apiService.get('/quotes').then(r => r.data))
  const quotes = (data?.quotes || []).map(toUI)

  const saveMutation = useMutation(
    (q) => q.id ? apiService.put(`/quotes/${q.id}`, toAPI(q)) : apiService.post('/quotes', toAPI(q)),
    {
      onSuccess: () => { toast.success('Quote saved'); queryClient.invalidateQueries(['quotes']); setEditing(null) },
      onError: (e) => toast.error(e.response?.data?.error || 'Failed to save quote'),
    }
  )
  const deleteMutation = useMutation(
    (id) => apiService.delete(`/quotes/${id}`),
    { onSuccess: () => { toast.success('Quote deleted'); queryClient.invalidateQueries(['quotes']) }, onError: () => toast.error('Failed to delete quote') }
  )
  const duplicateMutation = useMutation(
    (q) => apiService.post('/quotes', toAPI({ ...q, id: null, number: `${q.number} copy`, status: 'draft' })),
    { onSuccess: () => { toast.success('Quote duplicated'); queryClient.invalidateQueries(['quotes']) }, onError: () => toast.error('Failed to duplicate quote') }
  )

  const counts = {
    all: quotes.length,
    draft: quotes.filter(q => q.status === 'draft').length,
    sent: quotes.filter(q => q.status === 'sent').length,
    accepted: quotes.filter(q => q.status === 'accepted').length,
  }
  const wonValue = quotes.filter(q => q.status === 'accepted').reduce((s, q) => s + quoteTotal(q), 0)
  const filtered = quotes
    .filter(q => filter === 'all' || q.status === filter)
    .filter(q => {
      const t = search.trim().toLowerCase()
      if (!t) return true
      return (q.number || '').toLowerCase().includes(t) || (q.to?.name || '').toLowerCase().includes(t)
    })

  const startNew = () => {
    setEditing({
      id: null,
      number: `ORB-${String(quotes.length + 1).padStart(4, '0')}`,
      status: 'draft', currency: '£', issueDate: new Date().toISOString().slice(0, 10),
      from: { ...ORBITH_FROM }, to: { name: '', email: '', phone: '' },
      items: [{ desc: '', sublines: [], qty: 1, price: 0 }],
      notes: 'Cancel anytime, no fixed term.',
    })
  }

  const onDelete = async (q) => {
    const ok = await nsConfirm({
      tone: 'danger', title: 'Delete quote?',
      message: `“${q.number || 'Untitled'}” for ${q.to?.name || 'this client'} will be permanently removed.`,
      confirmLabel: 'Delete quote',
    })
    if (ok) deleteMutation.mutate(q.id)
  }

  if (editing) {
    return <QuoteEditor quote={editing} saving={saveMutation.isLoading} onCancel={() => setEditing(null)} onSave={(q) => saveMutation.mutate(q)} onDownload={downloadQuotePdf} />
  }

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Sales</div>
          <h1>Quotes</h1>
          <div className="subtitle">{quotes.length} quotes · {money(wonValue, '£')} accepted</div>
        </div>
        <div className="flex gap-2 items-center">
          <button className="btn btn-primary" onClick={startNew}><I.plus /> New quote</button>
        </div>
      </div>

      <div className="page-body">
        <div className="bento" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          <MiniStat label="Total" value={counts.all} icon="file" />
          <MiniStat label="Draft" value={counts.draft} icon="edit" />
          <MiniStat label="Sent" value={counts.sent} icon="send" tone="info" />
          <MiniStat label="Accepted" value={counts.accepted} icon="check" tone="success" />
        </div>

        <div className="filter-bar">
          <I.search style={{ color: 'var(--text-muted)', fontSize: 16 }} />
          <input className="input" placeholder="Search quotes…" value={search} onChange={e => setSearch(e.target.value)} />
          <div className="vdiv" style={{ height: 22 }} />
          <div className="segmented">
            {[['all', 'All', counts.all], ['draft', 'Draft', counts.draft], ['sent', 'Sent', counts.sent], ['accepted', 'Accepted', counts.accepted]].map(([k, l, n]) => (
              <button key={k} className={filter === k ? 'on' : ''} onClick={() => setFilter(k)}>
                {l} <span style={{ color: 'var(--text-faint)', marginLeft: 4, fontFamily: 'var(--font-mono)' }}>{n}</span>
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="card card-pad" style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><LoadingSpinner /></div>
        ) : filtered.length === 0 ? (
          <div className="card card-pad" style={{ padding: 48, textAlign: 'center' }}>
            <div className="icon-tile" style={{ width: 56, height: 56, fontSize: 26, margin: '0 auto 18px' }}><I.file /></div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>No quotes yet</div>
            <div style={{ color: 'var(--text-muted)', marginTop: 6, fontSize: 13 }}>Create your first quote to send to a client.</div>
            <button className="btn btn-primary" style={{ marginTop: 18 }} onClick={startNew}><I.plus /> New quote</button>
          </div>
        ) : (
          <div className="sites-table">
            <div className="row header" style={{ gridTemplateColumns: QCOLS }}>
              <div>Quote</div><div>Client</div><div>Status</div><div>Issued</div><div>Total</div><div></div>
            </div>
            {filtered.map(q => (
              <div key={q.id} className="row" style={{ gridTemplateColumns: QCOLS }}>
                <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>{q.number}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="avatar avatar-grad-4" style={{ width: 26, height: 26, fontSize: 10 }}>{initials(q.to?.name)}</div>
                  <span className="truncate">{q.to?.name || '—'}</span>
                </div>
                <div><span className={`badge ${q.status === 'draft' ? '' : `badge-${QSTATUS_TONE[q.status]}`}`}><span className="badge-dot" /> {QSTATUS_LABEL[q.status] || 'Draft'}</span></div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>{fmtDate(q.issueDate)}</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>{money(quoteTotal(q), q.currency || '£')}</div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="site-action" title="Edit" onClick={() => setEditing(q)}><I.edit /></button>
                  <button className="site-action" title="Download PDF" onClick={() => downloadQuotePdf(q)}><I.upload /></button>
                  <button className="site-action" title="Duplicate" onClick={() => duplicateMutation.mutate(q)}><I.folder /></button>
                  <button className="site-action" title="Delete" onClick={() => onDelete(q)}><I.trash /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

/* ---------------- editor ---------------- */
function QuoteEditor({ quote: initial, saving, onCancel, onSave, onDownload }) {
  const [quote, setQuote] = useState(initial)
  const set = (patch) => setQuote(q => ({ ...q, ...patch }))
  const setFrom = (patch) => setQuote(q => ({ ...q, from: { ...q.from, ...patch } }))
  const setTo = (patch) => setQuote(q => ({ ...q, to: { ...q.to, ...patch } }))
  const setItem = (i, patch) => setQuote(q => ({ ...q, items: q.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }))
  const addItem = () => setQuote(q => ({ ...q, items: [...q.items, { desc: '', sublines: [], qty: 1, price: 0 }] }))
  const removeItem = (i) => setQuote(q => ({ ...q, items: q.items.filter((_, idx) => idx !== i) }))

  const save = () => {
    if (!quote.number.trim()) return toast.error('Add a quote number first')
    if (!quote.to?.name?.trim()) return toast.error('Add a client name first')
    onSave(quote)
  }

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <button className="btn btn-sm" style={{ alignSelf: 'flex-start', marginBottom: 4 }} onClick={onCancel}>
            <I.arrowRight style={{ transform: 'rotate(180deg)' }} /> Back
          </button>
          <div className="eyebrow">Workspace · Sales</div>
          <h1>{initial.id ? 'Edit quote' : 'New quote'}</h1>
          <div className="subtitle">Fill in the details — the preview on the right updates as you type.</div>
        </div>
        <div className="flex gap-2 items-center">
          <button className="btn" onClick={() => onDownload(quote)}><I.upload /> Download PDF</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <LoadingSpinner size="sm" /> : <><I.check /> Save quote</>}
          </button>
        </div>
      </div>

      <div className="page-body">
        <div className="quote-edit-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, alignItems: 'flex-start' }}>
          {/* Form column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="card card-pad">
              <h2 className="h2" style={{ marginBottom: 14 }}>Quote details</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <NSField label="Quote number"><input className="input" value={quote.number} onChange={e => set({ number: e.target.value })} /></NSField>
                <NSField label="Status">
                  <NSSelect value={quote.status} onChange={v => set({ status: v })}
                    options={[
                      { value: 'draft', label: 'Draft' },
                      { value: 'sent', label: 'Sent' },
                      { value: 'accepted', label: 'Accepted' },
                      { value: 'declined', label: 'Declined' },
                    ]} />
                </NSField>
                <NSField label="Currency">
                  <NSSelect value={quote.currency} onChange={v => set({ currency: v })}
                    options={Object.keys(CURRENCY).map(c => ({ value: c, label: `${c} ${CURRENCY[c]}` }))} />
                </NSField>
              </div>
              <div style={{ marginTop: 12 }}>
                <NSField label="Issue date"><NSDatePicker value={quote.issueDate} onChange={v => set({ issueDate: v })} /></NSField>
              </div>
            </div>

            <div className="card card-pad">
              <h2 className="h2" style={{ marginBottom: 14 }}>From</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <NSField label="Agency name"><input className="input" value={quote.from?.name || ''} onChange={e => setFrom({ name: e.target.value })} /></NSField>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <NSField label="Email"><input className="input" value={quote.from?.email || ''} onChange={e => setFrom({ email: e.target.value })} /></NSField>
                  <NSField label="Phone"><input className="input" value={quote.from?.phone || ''} onChange={e => setFrom({ phone: e.target.value })} /></NSField>
                  <NSField label="Country"><input className="input" value={quote.from?.country || ''} onChange={e => setFrom({ country: e.target.value })} /></NSField>
                  <NSField label="Website"><input className="input" value={quote.from?.website || ''} onChange={e => setFrom({ website: e.target.value })} /></NSField>
                </div>
              </div>
            </div>

            <div className="card card-pad">
              <h2 className="h2" style={{ marginBottom: 14 }}>To</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <NSField label="Client name"><input className="input" value={quote.to?.name || ''} onChange={e => setTo({ name: e.target.value })} /></NSField>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <NSField label="Email" hint="optional"><input className="input" value={quote.to?.email || ''} onChange={e => setTo({ email: e.target.value })} /></NSField>
                  <NSField label="Phone" hint="optional"><input className="input" value={quote.to?.phone || ''} onChange={e => setTo({ phone: e.target.value })} /></NSField>
                </div>
              </div>
            </div>

            <div className="card card-pad">
              <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
                <h2 className="h2">Line items</h2>
                <button className="btn btn-sm" onClick={addItem}><I.plus /> Add item</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {quote.items.map((it, i) => (
                  <div key={i} style={{ padding: '14px', border: '1px solid var(--border)', borderRadius: 10, position: 'relative' }}>
                    <button className="site-action" style={{ position: 'absolute', top: 10, right: 10 }} title="Remove" onClick={() => removeItem(i)}><I.x /></button>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingRight: 30 }}>
                      <NSField label="Description"><input className="input" value={it.desc} onChange={e => setItem(i, { desc: e.target.value })} /></NSField>
                      <NSField label="Sub-details" hint="one per line, optional">
                        <textarea className="input" rows={3} style={{ height: 'auto', resize: 'vertical', padding: '9px 12px' }}
                          value={(it.sublines || []).join('\n')}
                          onChange={e => setItem(i, { sublines: e.target.value.split('\n') })} />
                      </NSField>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <NSField label="Qty"><input type="number" min="1" className="input" value={it.qty} onChange={e => setItem(i, { qty: e.target.value })} /></NSField>
                        <NSField label={`Price (${quote.currency})`}><input type="number" min="0" step="0.01" className="input" value={it.price} onChange={e => setItem(i, { price: e.target.value })} /></NSField>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card card-pad">
              <h2 className="h2" style={{ marginBottom: 14 }}>Notes</h2>
              <textarea className="input" rows={3} style={{ height: 'auto', resize: 'vertical', padding: '9px 12px' }}
                value={quote.notes || ''} onChange={e => set({ notes: e.target.value })} />
            </div>
          </div>

          {/* Live preview column */}
          <div style={{ position: 'sticky', top: 14 }}>
            <QuotePaper quote={quote} />
          </div>
        </div>
      </div>
    </>
  )
}
